import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '@saas-kit/ui';

import type { PermissionRow } from './api';

interface Props {
  all: PermissionRow[];
  picked: Set<string>;
  onToggle: (permissionKey: string, on: boolean) => void;
  disabled?: boolean;
}

export function PermissionGrid({
  all,
  picked,
  onToggle,
  disabled = false,
}: Props): JSX.Element {
  const { t } = useTranslation('rbac');

  const grouped = useMemo(() => {
    const map = new Map<string, PermissionRow[]>();
    for (const permission of all) {
      const list = map.get(permission.module) ?? [];
      list.push(permission);
      map.set(permission.module, list);
    }
    return [...map.entries()]
      .map(
        ([module, permissions]) =>
          [
            module,
            [...permissions].sort((a, b) => a.key.localeCompare(b.key)),
          ] as const,
      )
      .sort(([a], [b]) => a.localeCompare(b));
  }, [all]);

  if (grouped.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('perms.no_permissions')}</p>;
  }

  return (
    <div className="grid gap-3 md:grid-cols-2">
      {grouped.map(([module, permissions]) => {
        const enabledCount = permissions.filter((permission) =>
          picked.has(permission.key),
        ).length;
        return (
          <fieldset
            key={module}
            className="rounded-md border border-border bg-background p-3"
          >
            <legend className="flex items-center gap-2 px-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              <span>{module}</span>
              <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
                {enabledCount}/{permissions.length}
              </span>
            </legend>
            <ul className="space-y-2">
              {permissions.map((permission) => {
                const checked = picked.has(permission.key);
                const id = `perm-${permission.key}`;
                return (
                  <li key={permission.key} className="flex items-start gap-2">
                    <Checkbox
                      id={id}
                      checked={checked}
                      disabled={disabled}
                      onChange={(event) => onToggle(permission.key, event.target.checked)}
                      className="mt-0.5"
                    />
                    <label htmlFor={id} className="flex-1 cursor-pointer text-sm leading-tight">
                      <span className="font-mono text-xs text-foreground" dir="ltr">
                        {permission.key}
                      </span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        {permission.description}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </fieldset>
        );
      })}
    </div>
  );
}
